import React, { useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { FirmwareType } from '../../../../support/firmware/firmwarePage'
import { Button } from '../../../common/components/Button'
import Input from '../../../common/components/Input'
import TextArea from '../../../common/components/TextArea'
import { firmwareInputValidation } from '../../../common/constants/FormValidationSchemas'
import isDawn from '../../../lib/isDawn'
import useText from '../../../common/hooks/useText'
import isLuna from '../../../lib/isLuna'

const SWrapper = styled.div`
    border: 1px solid #e6e6e6;
    border-radius: 8px;
    padding: 25px 25px 45px 25px;
`

const STitle = styled.h3`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    margin: 0 0 1em 0;
`

const STitleFirmware = styled.span`
    font-family: ${({ theme }) => theme.font.name};
    font-size: 18px;
    font-weight: 300;
    line-height: 24px;
`

const SButtons = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 2.5em;
`

export interface UploadFirmwareModel {
    productId?: number
    firmwareType?: FirmwareType
    firmwareVersion?: number
    description: string
    ocppEnabledFirmware: boolean
    file: unknown
}

export type UploadFirmwarePanelProps = {
    productName: string
    firmware: UploadFirmwareModel
    errors?: unknown
    onSubmit: (data: unknown) => void
}

export type UploadFirmwareFormData = {
    firmwareVersion: string | number
    description: string
    file: unknown
}

const UploadFirmwarePanel = ({
    productName,
    firmware,
    errors,
    onSubmit,
}: UploadFirmwarePanelProps): React.ReactElement => {
    const {
        handleSubmit,
        register,
        formState: { errors: formErrors, isValid },
        reset,
    } = useForm<UploadFirmwareFormData>({ mode: 'onChange', delayError: 500 })
    const text = useText()
    const fwValidation = useMemo(() => firmwareInputValidation(productName, true), [productName])

    const submit = async (formValues: UploadFirmwareFormData) => {
        onSubmit({ ...formValues, file: formValues.file[0] })
    }

    const clearForm = useCallback(() => {
        reset({
            firmwareVersion: '',
            description: '',
            file: null,
        })
    }, [reset])

    useEffect(() => {
        clearForm()
    }, [clearForm])

    return (
        <SWrapper>
            <STitle data-testid="upload-title">
                Upload firmware {productName} <STitleFirmware>{firmware.firmwareType}</STitleFirmware>
            </STitle>

            <div className="form-group">
                <Input
                    id="input-version"
                    data-testid="input-version"
                    type={isDawn(productName) || isLuna(productName) ? 'text' : 'number'}
                    labelText="Version number"
                    error={formErrors.firmwareVersion?.message}
                    {...(!isDawn(productName) && { step: 1 })}
                    {...register('firmwareVersion', fwValidation)}
                />
            </div>
            <div className="form-group">
                <TextArea
                    data-testid="textarea"
                    error={formErrors.description?.message}
                    labelText="Description"
                    {...register('description', { required: 'Description is required' })}
                />
            </div>
            <div className="form-group">
                <Input
                    id="input-file"
                    data-testid="input-file"
                    error={errors?.file}
                    type="file"
                    accept=".zfw,.fw,.img,.enc"
                    {...register('file', { required: 'File is required' })}
                />
            </div>
            <SButtons>
                <Button
                    onClick={clearForm}
                    data-testid="btn-clear"
                    label={text('btnClear')}
                    size="sm"
                    primary={false}
                />
                <Button
                    onClick={handleSubmit(submit)}
                    data-testid="btn-upload"
                    label={text('btnUpload')}
                    size="sm"
                    disabled={!isValid}
                />
            </SButtons>
        </SWrapper>
    )
}

export default UploadFirmwarePanel
